import { useCallback, useRef, useState } from 'react';
import type { SourceScope } from '@photos/shared';
import { useNav } from '../api/hooks';
import { useEscape } from '../hooks/useEscape';
import { useOutsideClick } from '../hooks/useOutsideClick';
import { fmtCount } from '../lib/format';
import { useLibraryState } from '../state/libraryParams';
import { Icon, type IconName } from './Icon';

export const SOURCES: Record<SourceScope, { label: string; icon: IconName; desc: string }> = {
  glean: { label: 'Glean', icon: 'folder', desc: 'Uploaded by the media team' },
  flock: { label: 'Flock', icon: 'flock', desc: 'Posted by members in the Flock app' },
  all: { label: 'Both', icon: 'grid', desc: 'Glean + Flock together' },
};

const SRC_BG: Record<SourceScope, string> = {
  glean: '#2b5bd0',
  flock: '#c2620c',
  all: 'linear-gradient(135deg,#2b5bd0,#6b4fc7 50%,#c2620c)',
};

function SourceOption({ k, current, open, onPick }: { k: SourceScope; current: SourceScope; open: boolean; onPick(k: SourceScope): void }) {
  const o = SOURCES[k];
  const nav = useNav(k, open);
  return (
    <button
      type="button"
      role="menuitemradio"
      aria-checked={k === current}
      className={`srcopt${k === current ? ' on' : ''}`}
      onClick={() => onPick(k)}
    >
      <span className="sdot" style={{ background: SRC_BG[k] }}>
        <Icon name={o.icon} />
      </span>
      <span>
        <b>{o.label}</b>
        <small>
          {o.desc}
          {nav.data ? ` · ${fmtCount(nav.data.total)} photos` : ''}
        </small>
      </span>
      {k === current && <Icon name="check" />}
    </button>
  );
}

/**
 * Glean / Flock / Both. Opening only toggles the menu (the button's contents never re-render on open, which was
 * the prototype's dropdown bug); clicks stop propagating so the outside-click handler doesn't close it again.
 */
export function SourceMenu({ onOpen, variant = 'button' }: { onOpen?: () => void; variant?: 'button' | 'title' }) {
  const { state, go } = useLibraryState();
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  const refs = useRef([wrap]).current;
  const close = useCallback(() => setOpen(false), []);
  useOutsideClick(refs, close, open);
  useEscape(close, open);
  const v = SOURCES[state.source];
  const title = variant === 'title';

  const pick = (k: SourceScope) => {
    setOpen(false);
    if (k !== state.source) go({ source: k });
  };

  return (
    <div className={`srcwrap${title ? ' title' : ''}`} id="srcWrap" ref={wrap}>
      <button
        type="button"
        className={title ? 'srctitle' : 'srcbtn'}
        id="srcBtn"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Photo source: ${v.label}`}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => !o);
          onOpen?.();
        }}
      >
        {title ? (
          <>
            <span className="slbl">{v.label}</span>
            <Icon name="chev" />
          </>
        ) : (
          <>
            <span className="sdot" style={{ background: SRC_BG[state.source] }}>
              <Icon name={v.icon} />
            </span>
            <span className="slbl">{v.label}</span>
            <Icon name="chev" />
          </>
        )}
      </button>
      <div className={`menu${open ? ' open' : ''}`} id="srcMenu" role="menu" onClick={(e) => e.stopPropagation()}>
        <div className="lbl">Show photos from</div>
        {(Object.keys(SOURCES) as SourceScope[]).map((k) => (
          <SourceOption key={k} k={k} current={state.source} open={open} onPick={pick} />
        ))}
      </div>
    </div>
  );
}
